import * as Sentry from "@sentry/browser";

const EMERGENCY = 0;
const ALERT = 1;
const CRITICAL = 2;
const ERROR = 3;
const WARNING = 4;
const NOTICE = 5;
const INFO = 6;
const DEBUG = 7;

export type TLogEvent = { time: number; level: number; message: string; context: TLogContext };

type TLogContext = {
    isImportant?: boolean;
    actions?: { title: string; action: () => void }[];
    url?: string;
    code?: number;
    copyable?: boolean;
    details?: any[];
};

const RESERVATION_MOVED_TOO_QUICKLY_CODE = -9065;

// Events carrying one of these codes are suppressed - neither presented nor reported.
const IGNORE_CODES = [RESERVATION_MOVED_TOO_QUICKLY_CODE];

export const isLogIgnored = (context: TLogContext | undefined) =>
    context?.code !== undefined && IGNORE_CODES.includes(context.code);

// Sentry severity for the levels we report. Anything less severe than ERROR is not reported.
const toSentryLevel = (level: number): Sentry.SeverityLevel | null => {
    if (level <= CRITICAL) {
        return "fatal";
    }
    if (level === ERROR) {
        return "error";
    }
    return null;
};

const report = (message: string, context: TLogContext, level: number) => {
    const sentryLevel = toSentryLevel(level);
    if (sentryLevel === null || isLogIgnored(context)) {
        return;
    }
    Sentry.captureMessage(message, {
        level: sentryLevel,
        extra: {
            isImportant: level < WARNING || context.isImportant,
            url: context.url,
            details: context.details,
        },
        tags: { method: "log" },
    });
};

const listeners: {
    listener: (event: TLogEvent) => any;
    levels: number[];
}[] = [];

const notify = (newEvent: TLogEvent) => {
    const callbacks = listeners
        .filter((item) => item.levels.indexOf(newEvent.level) !== -1)
        .map((item) => item.listener);
    callbacks.forEach((callback) => callback(newEvent));
};

const log = (message, context: TLogContext | undefined, level: number) => {
    const logEvent: TLogEvent = {
        time: Date.now(),
        level,
        message,
        context: context || {},
    };
    report(logEvent.message, logEvent.context, level);
    notify(logEvent);
};

const emergency = (message: string, context?: TLogContext) => {
    log(message, context, EMERGENCY);
};

const alert = (message: string, context?: TLogContext) => {
    log(message, context, ALERT);
};

const critical = (message: string, context?: TLogContext) => {
    log(message, context, CRITICAL);
};

const error = (message?: string, context?: TLogContext) => {
    log(message, context, ERROR);
};

const warning = (message?: string, context?: TLogContext) => {
    log(message, context, WARNING);
};

const notice = (message: string, context?: TLogContext) => {
    log(message, context, NOTICE);
};

const info = (message: string, context?: TLogContext) => {
    log(message, context, INFO);
};

const debug = (message: string, context?: TLogContext) => {
    log(message, context, DEBUG);
};

/** Returns a function that unregisters the listener again. */
const addListener = (listener: (newEvent: TLogEvent) => void, levels: number[]) => {
    const entry = {
        listener,
        levels: Array.isArray(levels) ? levels : [levels],
    };
    listeners.push(entry);

    return () => {
        const index = listeners.indexOf(entry);
        if (index !== -1) {
            listeners.splice(index, 1);
        }
    };
};

export default {
    listen: addListener,
    emergency,
    alert,
    critical,
    error,
    warning,
    notice,
    info,
    debug,
    EMERGENCY,
    ALERT,
    CRITICAL,
    ERROR,
    WARNING,
    NOTICE,
    INFO,
    DEBUG,
    ALL: [DEBUG, INFO, NOTICE, WARNING, ERROR, CRITICAL, ALERT, EMERGENCY],
    // Errors the user cannot recover from. Presented in a blocking modal by
    // NonRecoverableErrorModal.
    NON_RECOVERABLE_LEVELS: [CRITICAL, ALERT, EMERGENCY],
    // Everything the user can recover from. Presented by the notification/message bar listener.
    // DEBUG is noise outside of local development, so it is only included there.
    RECOVERABLE_LEVELS:
        process.env.NODE_ENV === "development"
            ? [DEBUG, INFO, NOTICE, WARNING, ERROR]
            : [INFO, NOTICE, WARNING, ERROR],
    ALL_LOADS_FINISHED_MSG: "allLoadsFinished",
};
